import {Box, FAQ, FAQGroup, Grid, InlineLink, Section, Text} from '@primer/react-brand/esm'
import {ArrowUpIcon} from '@primer/octicons-react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import overviewStyles from '../../OverviewTemplate.module.css'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {useBrandedDivider} from '../../../hooks/useBrandedDivider'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroHeading,
  footnotes,
}: OutroSectionProps) => {
  const groupsWithFaqs = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )
  const singleGroup = groupsWithFaqs && groupsWithFaqs.length === 1 ? groupsWithFaqs[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const {brand} = settings?.fields || {}

  const {hasBrandedDivider, BrandedDivider} = useBrandedDivider({
    // Special case where the branded divider is not shown in the Outro section if it's a Copilot page.
    // We need to align on how to make this consistent across all Overview pages, but this is here for
    // now in order to support the current look of the two Overview template pages.
    enabled: brand !== 'Copilot',
  })

  if (
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!groupsWithFaqs || groupsWithFaqs.length === 0)
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && <FlexSuiteOutroCustomerStories variant="logo-cards" stories={outroCustomerStories} />}

        {outroCtaBanner && (
          <FlexSuiteOutroCTA
            outroCtaHeading={outroCtaHeading}
            outroCtaDescription={outroCtaDescription}
            outroCtaOne={outroCtaOne}
            outroCtaTwo={outroCtaTwo}
          />
        )}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {groupsWithFaqs && groupsWithFaqs.length > 1 && (
          <FAQGroup variant="gridline" defaultSelectedIndex={0}>
            {outroHeading && (
              <FAQGroup.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQGroup.Heading>
            )}

            {groupsWithFaqs.map(group => {
              const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

              return (
                <FAQ key={group.sys.id} variant="gridline">
                  {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                  {completeFaqs.map((faq, index) => (
                    <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                      <FAQ.Question>{faq.fields.question}</FAQ.Question>
                      <FAQ.Answer>
                        <Text as="div" variant="muted">
                          {documentToReactComponents(faq.fields.answer)}
                        </Text>
                      </FAQ.Answer>
                    </FAQ.Item>
                  ))}
                </FAQ>
              )
            })}
          </FAQGroup>
        )}

        {singleGroup && (
          <FAQ key={singleGroup.sys.id} variant="gridline">
            {outroHeading && (
              <FAQ.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQ.Heading>
            )}
            {singleGroup.fields.heading && (
              <FAQ.Heading>{renderWithoutParagraphs(singleGroup.fields.heading)}</FAQ.Heading>
            )}
            {singleGroup.fields.faqs
              .filter(faq => faq.fields.question && faq.fields.answer)
              .map((faq, index) => (
                <FAQ.Item key={faq.sys.id} name={`faq-${singleGroup.sys.id}`} open={index === 0}>
                  <FAQ.Question>{faq.fields.question}</FAQ.Question>
                  <FAQ.Answer>
                    <Text as="div" variant="muted">
                      {documentToReactComponents(faq.fields.answer)}
                    </Text>
                  </FAQ.Answer>
                </FAQ.Item>
              ))}
          </FAQ>
        )}

        {footnotes && footnotes.length > 0 && (
          <Grid>
            <Grid.Column>
              <Box
                paddingBlockStart={{narrow: 'spacious', regular: 64}}
                paddingBlockEnd={{narrow: 'spacious', regular: 64}}
              >
                <Text as="div" size="100" variant="muted">
                  <ol className={overviewStyles.footnotesList}>
                    {footnotes.map((footnote, index) => (
                      <li key={footnote.id} id={`footnote-${index + 1}`} className={overviewStyles.footnotesItem}>
                        {renderWithoutParagraphs(footnote.content)}{' '}
                        <InlineLink
                          href="#pricing-section"
                          aria-label={`Back to pricing section (footnote ${index + 1})`}
                        >
                          <ArrowUpIcon size={12} />
                        </InlineLink>
                      </li>
                    ))}
                  </ol>
                </Text>
              </Box>
            </Grid.Column>
          </Grid>
        )}
      </Section>
    </Box>
  )
}
